package dev.youndie.site.pages.blog

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initJvmAndNativeDifferencesPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("blog/JvmAndNativeDifferences.md", FrontMatterElement.Builder { addScalar("title", "Where Kotlin/Native and the JVM actually differ"); addScalar("searchTitle", "Kotlin/Native vs JVM behaviour differences, measured"); addScalar("date", "2026-09-12"); addScalar("description", "129 probes run on both runtimes: what agrees exactly, the seventeen rows that do not, why none of those seventeen has ever caused an outage, and why the ones that did are harder to test for."); }))
}

@Page("/blog/jvm-and-native-differences")
@Layout(".components.layouts.MarkdownLayout")
@Composable
fun JvmAndNativeDifferencesPage() {
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("If you develop on the JVM and ship a Kotlin/Native binary, you are relying on the two behaving the same. That reliance is usually backed by a checklist someone wrote from memory — hash order, string formatting, regex, dates — and not by a measurement. This is the measurement. The probe is ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/sborka/tree/main/docs/research/parity-probe") {
            org.jetbrains.compose.web.dom.Text("in sborka")
        }
        org.jetbrains.compose.web.dom.Text(": one ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("commonTest")
        }
        org.jetbrains.compose.web.dom.Text(" source set, 129 observations, each printing one row of a transcript, run on ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("jvm")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("macosArm64")
        }
        org.jetbrains.compose.web.dom.Text(" and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("linuxX64")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The short answer is that the standard library agrees far more than the checklists suggest, that the JDK version matters as often as the target does, and that the differences which have actually cost production time are not in the standard library at all.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-was-measured") }) {
        org.jetbrains.compose.web.dom.Text("What was measured")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Kotlin 2.4.10, kotlinx-serialization 1.11.0, kotlinx-datetime 0.8.0, JDK 25.0.2 and 17.0.20.1. Nothing is asserted in the probe: an assertion would encode which target is right, which is the question rather than the setup. ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("compare.py")
        }
        org.jetbrains.compose.web.dom.Text(" prints both values and labels neither a reference.")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-bash") }) { org.jetbrains.compose.web.dom.Text("""./run.sh jvm
./run.sh macosArm64            # linuxX64 on a Linux host
./compare.py results/<a> results/<b>
""") } }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-agrees-exactly") }) {
        org.jetbrains.compose.web.dom.Text("What agrees, exactly")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Forty-nine rows, zero differences, on every runtime and JDK tried:")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Class")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Rows")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Includes")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("Number formatting")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("17")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("0.1 + 0.2")
                    }
                    org.jetbrains.compose.web.dom.Text(", ")
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("1e23")
                    }
                    org.jetbrains.compose.web.dom.Text(", ")
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("1e-5")
                    }
                    org.jetbrains.compose.web.dom.Text(", ")
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("-0.0")
                    }
                    org.jetbrains.compose.web.dom.Text(", ")
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("Double.MIN_VALUE")
                    }
                    org.jetbrains.compose.web.dom.Text(", ")
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("1f/3f")
                    }
                    org.jetbrains.compose.web.dom.Text(", ")
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("toString(radix)")
                    }
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("Strings and Unicode")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("19")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("\"straße\".uppercase()")
                    }
                    org.jetbrains.compose.web.dom.Text(", the ")
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("ﬁ")
                    }
                    org.jetbrains.compose.web.dom.Text(" ligature, dotted ")
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("İ")
                    }
                    org.jetbrains.compose.web.dom.Text(", the ")
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("ǅ")
                    }
                    org.jetbrains.compose.web.dom.Text(" digraph, surrogate pairs, ")
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("sorted()")
                    }
                    org.jetbrains.compose.web.dom.Text(", ")
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("trim()")
                    }
                    org.jetbrains.compose.web.dom.Text(" on NBSP")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("Hashing")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("10")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("String.hashCode")
                    }
                    org.jetbrains.compose.web.dom.Text(" including non-BMP text, data classes, lists, maps, ")
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("Double")
                    }
                    org.jetbrains.compose.web.dom.Text(", ")
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("Long")
                    }
                    org.jetbrains.compose.web.dom.Text(", ")
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("Char")
                    }
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("Seeded randomness")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("3")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("Random(42)")
                    }
                    org.jetbrains.compose.web.dom.Text(" sequence, ")
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("nextDouble")
                    }
                    org.jetbrains.compose.web.dom.Text(", ")
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("shuffled")
                    }
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Twenty of the 22 regex probes agree too, including the constructs usually named as risks: ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("\\w")
        }
        org.jetbrains.compose.web.dom.Text(" on accented text, ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("\\d")
        }
        org.jetbrains.compose.web.dom.Text(" on Arabic-Indic digits, ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("\\p{L}")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("\\p{IsCyrillic}")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("[[:alpha:]]")
        }
        org.jetbrains.compose.web.dom.Text(", fixed and variable look-behind, named groups, back-references, possessive quantifiers and atomic groups.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-differs") }) {
        org.jetbrains.compose.web.dom.Text("What differs")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Seventeen rows of 129, on JDK 25:")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Probe")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("JVM")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Kotlin/Native")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("HashMap")
                    }
                    org.jetbrains.compose.web.dom.Text(" iteration order")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("epsilon,zeta,eta,alpha,delta,theta,beta,gamma")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("alpha,beta,gamma,delta,epsilon,zeta,eta,theta")
                    }
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("the same map through kotlinx-serialization")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("{\"a\":1,\"b\":3,\"z\":0,\"m\":2}")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("{\"z\":0,\"a\":1,\"m\":2,\"b\":3}")
                    }
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("1 / 0")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("ArithmeticException: / by zero")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("ArithmeticException: null")
                    }
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("\"abc\"[7]")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("StringIndexOutOfBoundsException")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("ArrayIndexOutOfBoundsException")
                    }
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("\"2147483648\".toInt()")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("NumberFormatException: For input string: …")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("NumberFormatException: null")
                    }
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("TimeZone.availableZoneIds.size")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("604")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("597 on macOS, 496 on Linux")
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Six of them are exception messages, and one of those changes the exception's ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("class")
        }
        org.jetbrains.compose.web.dom.Text(" — both are ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("IndexOutOfBoundsException")
        }
        org.jetbrains.compose.web.dom.Text(" subclasses, so a ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("catch")
        }
        org.jetbrains.compose.web.dom.Text(" on the base type is unaffected, but a ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("when (e)")
        }
        org.jetbrains.compose.web.dom.Text(" chain in common code takes a different branch.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Hash order is the only row with a silent consequence.")
        }
        org.jetbrains.compose.web.dom.Text(" Everything else surfaces as text a human reads. This one surfaces as different bytes in a payload, which matters the moment a golden file, a payload hash or an idempotency key touches it. Sorting at the boundary fixes it, and sorting is safe precisely because all nineteen string comparison probes agree.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("The timezone row is not a language difference.")
        }
        org.jetbrains.compose.web.dom.Text(" kotlinx-datetime reads the host's zone database on Kotlin/Native, and the JDK ships its own. In a container that makes the zone table a property of the base image. Every zone ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("offset")
        }
        org.jetbrains.compose.web.dom.Text(" probed — Moscow in 1990 and 2011, the Kiritimati date-line skip, a Berlin DST gap — agreed on every target.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-jdk-is-a-variable-as-often-as-the-target-is") }) {
        org.jetbrains.compose.web.dom.Text("The JDK is a variable, as often as the target is")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Run the same probe on JDK 17 and five rows move, two of which change what you would otherwise conclude:")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("Regex(\"\\bé\").find(\"é\")")
            }
            org.jetbrains.compose.web.dom.Text(" returns ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("null")
            }
            org.jetbrains.compose.web.dom.Text(" on JDK 25 and ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("é")
                }
                org.jetbrains.compose.web.dom.Text(" on JDK 17, which is what Kotlin/Native returns")
            }
            org.jetbrains.compose.web.dom.Text(". This is JDK-19 behaviour (JDK-8264160 aligned ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("\\b")
            }
            org.jetbrains.compose.web.dom.Text(" with the ASCII ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("\\w")
            }
            org.jetbrains.compose.web.dom.Text(" it is defined against), not a Kotlin/Native difference at all.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("1e23")
            }
            org.jetbrains.compose.web.dom.Text(" prints ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("9.999999999999999E22")
            }
            org.jetbrains.compose.web.dom.Text(" on JDK 17 and ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("1.0E23")
            }
            org.jetbrains.compose.web.dom.Text(" on JDK 25 — the shortest-representation fix from the same release. Native prints ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("1.0E23")
            }
            org.jetbrains.compose.web.dom.Text(", so on JDK 17 there are two double-formatting differences that do not exist on JDK 25.")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("So the count is 17 on JDK 25 and 18 on JDK 17, and they are not the same eighteen. A page that names Kotlin and its libraries but not the JDK is wrong about those rows in a way no reader can detect.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("where-the-differences-that-cost-something-actually-were") }) {
        org.jetbrains.compose.web.dom.Text("Where the differences that cost something actually were")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("None of the seventeen has caused an outage. The ones that did are platform APIs:")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.P {
                org.jetbrains.compose.web.dom.B {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("ktor-client-cio")
                    }
                    org.jetbrains.compose.web.dom.Text(" has no TLS on Kotlin/Native")
                }
                org.jetbrains.compose.web.dom.Text(" — ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("IllegalStateException: TLS sessions are not supported")
                }
                org.jetbrains.compose.web.dom.Text(". Outbound HTTPS silently never left the service. Fixed with ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("ktor-client-curl")
                }
                org.jetbrains.compose.web.dom.Text(" on native and CIO on the JVM through ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("expect")
                }
                org.jetbrains.compose.web.dom.Text("/")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("actual")
                }
                org.jetbrains.compose.web.dom.Text(".")
            }
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.P {
                org.jetbrains.compose.web.dom.B {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("ktor-server-compression")
                    }
                    org.jetbrains.compose.web.dom.Text(" is published for the JVM only")
                }
                org.jetbrains.compose.web.dom.Text(", so compression moves into the image build as pre-made ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text(".gz")
                }
                org.jetbrains.compose.web.dom.Text(" files.")
            }
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.P {
                org.jetbrains.compose.web.dom.B {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("Dispatchers.IO")
                    }
                    org.jetbrains.compose.web.dom.Text(" on Kotlin/Native is an extension property")
                }
                org.jetbrains.compose.web.dom.Text(" in package ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("kotlinx.coroutines")
                }
                org.jetbrains.compose.web.dom.Text(" and needs ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("import kotlinx.coroutines.IO")
                }
                org.jetbrains.compose.web.dom.Text(" of its own. Without that import the compiler resolves the internal member of the same name and reports \"it is internal\" — which is accurate, and reads exactly like a platform limitation. It is not one: with the import it compiles for ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("linuxX64")
                }
                org.jetbrains.compose.web.dom.Text(" and ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("macosArm64")
                }
                org.jetbrains.compose.web.dom.Text(" and ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("withContext(Dispatchers.IO)")
                }
                org.jetbrains.compose.web.dom.Text(" runs.")
            }
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.P {
                org.jetbrains.compose.web.dom.B {
                    org.jetbrains.compose.web.dom.Text("One library can be two libraries.")
                }
                org.jetbrains.compose.web.dom.Text(" sqlx4k's SQLite is the Rust driver on Kotlin/Native and Xerial's ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("sqlite-jdbc")
                }
                org.jetbrains.compose.web.dom.Text(" on the JVM, behind one API. They disagree about in-memory databases: the JVM half refuses a connection pool larger than one — rightly, since a second connection to ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text(":memory:")
                }
                org.jetbrains.compose.web.dom.Text(" is a second, empty database — and pinning the pool to one then deadlocks. A test suite that ran on the native target alone never met either half.")
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("And one that was true and no longer is: ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("InetSocketAddress(host, port)")
        }
        org.jetbrains.compose.web.dom.Text(" used not to resolve a hostname on Kotlin/Native, which left agents reporting through a Kubernetes service name silent from their first day. Re-probed at Ktor 3.5.2 through ktor-network's own ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("InetSocketAddress")
        }
        org.jetbrains.compose.web.dom.Text(", a TCP connect to a hostname succeeds on ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("macosArm64")
        }
        org.jetbrains.compose.web.dom.Text(" and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("linuxX64")
        }
        org.jetbrains.compose.web.dom.Text(" both. Which is the argument for measuring rather than listing: a hand-kept list of platform differences only ever grows, because nothing in it reports the day an entry stops being true.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("testing-for-the-platform-layer-is-harder-than-testing-for-the-stdlib") }) {
        org.jetbrains.compose.web.dom.Text("Testing for the platform layer is harder than testing for the stdlib")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The stdlib rows above are a transcript: run the same source twice, diff. The platform layer resists that, and it is worth knowing why before building a check for it.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("An assertion has to go through ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("the API the program uses")
        }
        org.jetbrains.compose.web.dom.Text(", not the one underneath it. The hostname failure was in ktor's ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("InetSocketAddress")
        }
        org.jetbrains.compose.web.dom.Text(", so a probe calling ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("getaddrinfo")
        }
        org.jetbrains.compose.web.dom.Text(" would have been green while a fleet of agents was silent.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("And some of it cannot be asserted hermetically at all. \"Does this engine support TLS\" looks checkable: make an HTTPS request to a socket that hangs up, and see whether the failure is a refusal. On Kotlin/Native with the curl engine it is not, because curl reports its ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("ordinary")
        }
        org.jetbrains.compose.web.dom.Text(" handshake failure as ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("IllegalStateException")
        }
        org.jetbrains.compose.web.dom.Text(" — the same type ktor uses to refuse TLS outright:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-text") }) { org.jetbrains.compose.web.dom.Text("""IllegalStateException: Connection failed … Reason: SSL connect error (CURLE_SSL_CONNECT_ERROR)
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("No exception type separates ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("reached the TLS layer and failed")
        }
        org.jetbrains.compose.web.dom.Text(" from ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("has no TLS at all")
        }
        org.jetbrains.compose.web.dom.Text(". Only the message text does, and a check resting on a sentence passes the day the sentence is reworded. What settles it is a service with a certificate to talk to — which is a stand, not a unit test.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("limits") }) {
        org.jetbrains.compose.web.dom.Text("Limits")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("macosArm64")
        }
        org.jetbrains.compose.web.dom.Text(" and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("linuxX64")
        }
        org.jetbrains.compose.web.dom.Text(" differ in one row of the 129 — the zone table — so the numbers above describe the runtimes rather than a host, with that one exception. The probe covers the standard library, the regex engine, kotlinx-serialization and kotlinx-datetime; it does not cover coroutines scheduling, file-system behaviour, or anything that needs a socket. The re-probed ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("InetSocketAddress")
        }
        org.jetbrains.compose.web.dom.Text(" result is a TCP connect, and UDP takes a different path to the same address type. Every number is a property of the versions named at the top and of nothing else, which is why the transcripts are committed beside the probe rather than quoted only here.")
    }
}
